import { forwardRef, type HTMLAttributes, type ReactNode } from "react";
import styles from "./List.module.css";

export interface ListProps extends HTMLAttributes<HTMLDivElement> {
  inset?: boolean;
  header?: ReactNode;
  footer?: ReactNode;
}

export const List = forwardRef<HTMLDivElement, ListProps>(function List(
  { inset = true, header, footer, className, children, ...rest },
  ref
) {
  return (
    <div
      ref={ref}
      className={[styles.list, inset ? styles.inset : "", className ?? ""].join(" ")}
      {...rest}
    >
      {header && <div className={styles.header}>{header}</div>}
      <div className={styles.group} role="list">
        {children}
      </div>
      {footer && <div className={styles.footer}>{footer}</div>}
    </div>
  );
});

export interface ListItemProps extends Omit<HTMLAttributes<HTMLDivElement>, "title"> {
  leading?: ReactNode;
  trailing?: ReactNode;
  title?: ReactNode;
  subtitle?: ReactNode;
  chevron?: boolean;
  onClick?: () => void;
  destructive?: boolean;
  divider?: "full" | "inset" | "none";
}

export const ListItem = forwardRef<HTMLDivElement, ListItemProps>(function ListItem(
  {
    leading,
    trailing,
    title,
    subtitle,
    chevron = false,
    onClick,
    destructive = false,
    divider = "inset",
    className,
    children,
    ...rest
  },
  ref
) {
  const interactive = Boolean(onClick);
  return (
    <div
      ref={ref}
      role={interactive ? "button" : "listitem"}
      tabIndex={interactive ? 0 : undefined}
      className={[
        styles.item,
        interactive ? styles.interactive : "",
        destructive ? styles.destructive : "",
        divider === "none" ? "" : styles[`divider-${divider}`],
        className ?? "",
      ].join(" ")}
      onClick={onClick}
      onKeyDown={(e) => {
        if (interactive && (e.key === "Enter" || e.key === " ")) {
          e.preventDefault();
          onClick?.();
        }
      }}
      {...rest}
    >
      {leading && <div className={styles.leading}>{leading}</div>}
      <div className={styles.content}>
        {title && <div className={styles.title}>{title}</div>}
        {subtitle && <div className={styles.subtitle}>{subtitle}</div>}
        {children}
      </div>
      {trailing && <div className={styles.trailing}>{trailing}</div>}
      {chevron && (
        <div className={styles.chevron} aria-hidden>
          <svg width="10" height="14" viewBox="0 0 10 14" fill="none">
            <path d="M1 1L7 7L1 13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </div>
      )}
    </div>
  );
});